/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormActions,
  FormFlowState,
} from '@gh/builder-shared';
import { Month, OfficeStatus } from '@gh/builder-shared-data';
import {
  ActionFlow,
  AppRouter,
  EnumDictionary,
  getDefaultEmail,
  getRouteInitData,
  TextDictionary,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DEFAULT_EMAIL_SUFFIX } from '@gh/config';
import {
  AddressAutocompleteComponent,
  AddressComponents,
  DialogService,
  FormMode,
  getControlValue,
} from '@gh/core-ui';
import { assign } from 'lodash';
import { SubOfficeDetailsBusinessService } from '../../resources/sub-office-details-business.service';
import { SubOfficeDetails } from '../../resources/sub-office-details.model';
import { SubOfficeDetailsFormState } from './sub-office-details-form.service';
import { OfficeListItem } from '../../../offices/resources/office-list-item.model';
import { OfficeDetails } from '../../../offices/resources/office-details.model';

@Component({
  standalone: false,
  selector: 'gh-sub-office-details',
  templateUrl: 'sub-office-details.component.html',
  styleUrls: ['sub-office-details.component.scss'],
  viewProviders: [SubOfficeDetailsFormState],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubOfficeDetailsComponent extends AbstractFormComponent<SubOfficeDetails> implements OnInit, AfterViewInit {
  @ViewChild(AddressAutocompleteComponent) addressAutocomplete: AddressAutocompleteComponent | undefined;

  franchiseeOffices: OfficeListItem[];
  officeStatuses: EnumDictionary<OfficeStatus>;
  parentOffice: OfficeDetails;
  states: TextDictionary;
  months: EnumDictionary<Month>;
  emailSuffix = DEFAULT_EMAIL_SUFFIX;
  isModifyingOn: boolean = false;
  subOfficeId: ID;
  showManualAddressFields = false;

  constructor(private route: ActivatedRoute,
    private router: AppRouter,
    private userContext: BuilderUserContext,
    private businessService: SubOfficeDetailsBusinessService,
    public form: SubOfficeDetailsFormState,
    private pipelines: FormActionPipelines,
    private commonActions: CommonActions,
    private formActions: FormActions,
    private dialogService: DialogService,
    private navigationService: BuilderNavigationService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  @ViewChild('companyNameField') fieldToFocus!: ElementRef;

  get isOfficeView(): boolean {
    return this.userContext.officeView;
  }

  get title(): string {
    return getControlValue(this.form.group, 'displayName');
  }

  get email(): string {
    return getDefaultEmail(getControlValue(this.form.group, 'email'));
  }

  get pageTitle(): string {
    return {
      [FormMode.CreateMode]: 'lbl_sub_office_create',
      [FormMode.EditMode]: 'lbl_sub_office_edit',
      [FormMode.ViewMode]: '',
    }[this.form.mode];
  }

  get completeFlow(): ActionFlow<FormFlowState<SubOfficeDetails>> {
    if (this.form.group.invalid) {
      this.checkAndShowManualAddressFields();
      this.form.group.markAllAsTouched();
    }
    return this.pipelines.complete({
      execute: this.commonActions.execute((state: FormFlowState<SubOfficeDetails>) => {
        if (this.form.createMode) {
          return this.businessService.create(state.form.submitValue);
        } else {
          return this.businessService.update(state.form.submitValue);
        }
      }),
      finish: (state) => {
        const { value, form } = state;
        this.isModifyingOn = false;
        if (this.form.createMode) {
          this.router.switchTo(['/setup/sub-offices', value.id]);
        } else {
          form.build(value);
        }
      },
    });
  }

  ngOnInit(): void {
    const { officeDetails, ...init } = getRouteInitData(this.route);

    assign(this, init);
    this.subOfficeId = officeDetails.id;
    this.form.build(officeDetails);
    if (this.parentOffice) {
      this.form.updateEmail(this.parentOffice?.email || '');
    }

    const hasAddressData = !!(this.form.group.get('street1')?.value ||
      this.form.group.get('street2')?.value ||
      this.form.group.get('city')?.value ||
      this.form.get('state')?.value ||
      this.form.group.get('postCode')?.value);
    this.showManualAddressFields = hasAddressData;
    this.cdr.markForCheck();
  }

  ngAfterViewInit(): void {
    if (this.form.mode === 1) this.fieldToFocus.nativeElement.focus();
    this.onAddressChange();
    this.cdr.detectChanges();
  }

  onApplyLicense(): void {
    this.form.updateLicenseState();
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'SUB_OFFICE',
      label: label ? label : 'lbl_menu_sub_offices',
    });
  }

  onOfficeSelect({ value }: any): void {
    const targetOffice = this.franchiseeOffices.find((office) => office.officeId === value);
    this.form.updateEmail(targetOffice?.email || '');
  }

  onAddressChange() {
    const street1 = this.form.group.get('street1')?.value || '';
    const street2 = this.form.group.get('street2')?.value || '';
    const city = this.form.group.get('city')?.value || '';
    const state = this.form.group.get('state')?.value || '';
    const postCode = this.form.group.get('postCode')?.value || '';

    const addressParts: string[] = [];

    const streetCombined = [street2, street1].filter(Boolean).join(' ');
    if (streetCombined) addressParts.push(streetCombined);

    if (city) addressParts.push(city);
    if (state) addressParts.push(state);
    if (postCode) addressParts.push(postCode);

    const newAddress = addressParts.join(', ');

    if (this.addressAutocomplete) {
      this.addressAutocomplete.inputValue = newAddress;
    }
  }

  onAddressSelected(components: AddressComponents): void {
    const streetParts: string[] = [];
    if (components.streetNo) streetParts.push(components.streetNo);
    if (components.street) streetParts.push(components.street);
    const street1Value = streetParts.join(' ').trim();

    this.form.group.patchValue({
      street1: street1Value || '',
      city: components.city || '',
      state: components.state || '',
      postCode: components.postalCode || ''
    });

    ['street1', 'city', 'state', 'postCode'].forEach(field => {
      const control = this.form.group.get(field);
      if (control) control.markAsDirty();
    });
  }

  private checkAndShowManualAddressFields(): void {
    if (this.showManualAddressFields) return;

    const addressFields = ['street1', 'city', 'state', 'postCode'];
    const hasAddressErrors = addressFields.some(field => {
      const control = this.form.group.get(field);
      return control && control.invalid;
    });

    if (hasAddressErrors) {
      this.showManualAddressFields = true;
    }
  }
}
